import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { toPng } from "html-to-image";
import { fetchDivisionData, fetchInsightOptions } from "../utils/insightsData";
import { buildMatchdays, buildWeeklyReport, divisionLabel, pad2, seasonLabel, selectHighlights } from "../utils/insights";
import { buildPredictions, forecastNextMatchday } from "../utils/predictions";
import PointsChart from "./insights/PointsChart";
import Poster, { POSTER_HEIGHT, POSTER_WIDTH } from "./insights/Poster";
import { C, Crest, FONT, FormPill, OddsBar, PredictedScore, formatMatchdayDate, inlineImages, joinNames, pct, toneColor } from "./insights/common";
import hslLogo from "../HslLogo.png";

const panel = {
  background: C.panel,
  border: `1px solid ${C.border}`,
  borderRadius: 16,
  padding: 16,
  minWidth: 0,
};

const sectionTitle = { color: C.muted, fontSize: 12, fontWeight: 800, letterSpacing: 1.6, margin: "0 0 12px" };

function Select({ label, value, onChange, children, disabled }) {
  return (
    <label style={{ display: "flex", flexDirection: "column", gap: 4, minWidth: 0, flex: "1 1 140px" }}>
      <span style={{ color: C.muted, fontSize: 11, fontWeight: 700, letterSpacing: 1.2 }}>{label}</span>
      <select
        value={value}
        disabled={disabled}
        onChange={(e) => onChange(e.target.value)}
        style={{
          background: C.panelHi,
          color: C.text,
          border: `1px solid ${C.border}`,
          borderRadius: 10,
          padding: "9px 10px",
          fontSize: 14,
          fontWeight: 600,
          fontFamily: FONT,
          width: "100%",
        }}
      >
        {children}
      </select>
    </label>
  );
}

function Button({ children, onClick, disabled, primary, ariaLabel }) {
  return (
    <button
      type="button"
      aria-label={ariaLabel}
      onClick={onClick}
      disabled={disabled}
      style={{
        background: primary ? C.accent : C.panelHi,
        color: primary ? C.bg : C.text,
        border: `1px solid ${primary ? C.accent : C.border}`,
        borderRadius: 10,
        padding: "9px 14px",
        fontSize: 14,
        fontWeight: 800,
        fontFamily: FONT,
        cursor: disabled ? "default" : "pointer",
        opacity: disabled ? 0.4 : 1,
        whiteSpace: "nowrap",
      }}
    >
      {children}
    </button>
  );
}

function Notice({ tone = "info", children }) {
  const color = tone === "warn" ? C.forfeit : tone === "error" ? C.loss : C.muted;
  return (
    <div style={{ ...panel, borderColor: color, color: C.text, fontSize: 14, lineHeight: 1.5, padding: "12px 14px" }}>
      {children}
    </div>
  );
}

function Movement({ change }) {
  if (change === null || change === undefined) return <span style={{ color: C.muted }}>–</span>;
  if (change === 0) return <span style={{ color: C.muted }}>●</span>;
  return (
    <span style={{ color: change > 0 ? C.win : C.loss, fontWeight: 800 }}>
      {change > 0 ? "▲" : "▼"}
      {Math.abs(change)}
    </span>
  );
}

function StandingsTable({ rows, compact }) {
  const cols = compact
    ? "34px minmax(120px, 1fr) 30px 30px 40px"
    : "36px minmax(170px, 1fr) repeat(6, 36px) 44px 44px 112px 36px";
  const headers = compact ? ["GP", "GD", "PTS"] : ["GP", "W", "D", "L", "GF", "GA", "GD", "PTS"];
  return (
    <div role="table" aria-label="Standings" style={{ fontSize: 14 }}>
      <div role="row" style={{ display: "grid", gridTemplateColumns: cols, color: C.muted, fontSize: 11, fontWeight: 800, letterSpacing: 1, padding: "0 4px 8px" }}>
        <span>#</span>
        <span>TEAM</span>
        {headers.map((h) => (
          <span key={h} style={{ textAlign: "center", color: h === "PTS" ? C.accent : C.muted }}>
            {h}
          </span>
        ))}
        {!compact && <span style={{ textAlign: "right" }}>FORM</span>}
        {!compact && <span style={{ textAlign: "right" }}>+/-</span>}
      </div>
      {rows.map((r) => {
        const vals = compact
          ? [r.played, r.goalDifference, r.points]
          : [r.played, r.won, r.drawn, r.lost, r.goalsFor, r.goalsAgainst, r.goalDifference, r.points];
        return (
          <div
            role="row"
            key={r.teamId}
            style={{
              display: "grid",
              gridTemplateColumns: cols,
              alignItems: "center",
              padding: "8px 4px",
              borderTop: `1px solid ${C.border}`,
              background: r.position === 1 && r.played > 0 ? C.faint : undefined,
            }}
          >
            <span style={{ color: r.position === 1 && r.played > 0 ? C.accent : C.muted, fontWeight: 900 }}>
              {r.tied ? "=" : ""}
              {r.position}
            </span>
            <span style={{ display: "flex", alignItems: "center", gap: 8, minWidth: 0 }}>
              <Crest url={r.logoUrl} name={r.name} size={24} />
              <span style={{ flex: 1, minWidth: 0, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", fontWeight: 700 }}>{r.name}</span>
            </span>
            {vals.map((v, i) => {
              const isPts = i === vals.length - 1;
              const isGd = i === vals.length - 2;
              return (
                <span
                  key={i}
                  style={{
                    textAlign: "center",
                    color: isPts ? C.text : isGd && v > 0 ? C.win : isGd && v < 0 ? C.loss : C.muted,
                    fontWeight: isPts ? 900 : 600,
                  }}
                >
                  {isGd && v > 0 ? `+${v}` : v}
                </span>
              );
            })}
            {!compact && (
              <span style={{ display: "flex", gap: 3, justifyContent: "flex-end" }}>
                {r.form.map((f, i) => (
                  <FormPill key={i} r={f} size={18} />
                ))}
              </span>
            )}
            {!compact && (
              <span style={{ textAlign: "right", fontSize: 12 }}>
                <Movement change={r.positionChange} />
              </span>
            )}
          </div>
        );
      })}
    </div>
  );
}

function RaceList({ entries, teamsById, size = 26 }) {
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
      {entries.map((p) => (
        <div key={p.id || p.playerId} style={{ display: "flex", alignItems: "center", gap: 8, minWidth: 0 }}>
          <span style={{ width: 16, color: p.rank === 1 ? C.gold : C.muted, fontWeight: 900, fontSize: 13 }}>{p.rank}</span>
          <Crest url={p.logoUrl ?? teamsById?.[p.teamId]?.logo_url} name={p.sub ?? teamsById?.[p.teamId]?.name} size={size} />
          <span style={{ flex: 1, minWidth: 0 }}>
            <span style={{ display: "block", fontWeight: 800, fontSize: 14, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{p.name}</span>
            <span style={{ display: "block", color: C.muted, fontSize: 11 }}>{p.sub ?? teamsById?.[p.teamId]?.name}</span>
          </span>
          <span style={{ color: C.gold, fontWeight: 900, fontSize: 18 }}>{p.goals}</span>
        </div>
      ))}
    </div>
  );
}

function HighlightCard({ h }) {
  const tone = toneColor(h.tone);
  if (h.key === "golden-boot") {
    return (
      <div style={{ ...panel, borderTop: `3px solid ${tone}`, display: "flex", flexDirection: "column", gap: 8 }}>
        <div style={{ color: tone, fontSize: 11, fontWeight: 800, letterSpacing: 1.4 }}>{h.label}</div>
        <RaceList entries={h.entities} size={24} />
        <div style={{ color: C.muted, fontSize: 11 }}>{h.detail}</div>
      </div>
    );
  }
  return (
    <div style={{ ...panel, borderTop: `3px solid ${tone}`, display: "flex", flexDirection: "column", gap: 6 }}>
      <div style={{ color: tone, fontSize: 11, fontWeight: 800, letterSpacing: 1.4 }}>{h.label}</div>
      <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
        <span style={{ fontSize: 32, fontWeight: 900, lineHeight: 1 }}>{h.value}</span>
        <span style={{ display: "flex", marginLeft: "auto" }}>
          {h.entities.slice(0, 4).map((e, i) => (
            <Crest key={e.id} url={e.logoUrl} name={e.name} size={30} style={{ marginLeft: i ? -8 : 0 }} />
          ))}
        </span>
      </div>
      <div style={{ color: C.muted, fontSize: 12 }}>{h.detail}</div>
      {h.entities.length > 0 && (
        <div style={{ fontSize: 13, fontWeight: 700 }}>
          {joinNames(h.entities.map((e) => (e.sub ? `${e.name} (${e.sub})` : e.name)))}
        </div>
      )}
    </div>
  );
}

function Predictions({ predictions, teamsById }) {
  if (!predictions) return null;
  const { nextMatchday, nextGames, table } = predictions;
  return (
    <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(min(100%, 420px), 1fr))", gap: 16 }}>
      {nextMatchday && nextGames.length > 0 && (
        <section style={panel}>
          <h2 style={sectionTitle}>
            PREDICTIONS · MATCHDAY {nextMatchday.matchday}{" "}
            <span style={{ fontWeight: 600 }}>· {formatMatchdayDate(nextMatchday.date, { month: "short", day: "numeric" })}</span>
          </h2>
          {nextGames.map((g) => {
            const home = teamsById[g.homeTeamId] || { name: "TBD" };
            const away = teamsById[g.awayTeamId] || { name: "TBD" };
            const fav = g.home > g.away + 0.1 ? "home" : g.away > g.home + 0.1 ? "away" : null;
            return (
              <div key={g.fixtureId} style={{ padding: "10px 0", borderTop: `1px solid ${C.border}`, display: "flex", flexDirection: "column", gap: 6 }}>
                <div style={{ display: "grid", gridTemplateColumns: "1fr auto 1fr", alignItems: "center", gap: 8 }}>
                  <span style={{ display: "flex", alignItems: "center", gap: 6, minWidth: 0 }}>
                    <Crest url={home.logo_url} name={home.name} size={22} />
                    <span style={{ fontSize: 13, fontWeight: fav === "home" ? 800 : 600, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{home.name}</span>
                  </span>
                  <span style={{ textAlign: "center", color: C.muted, fontSize: 12 }}>
                    likely <PredictedScore score={g.likelyScore} size={13} />
                  </span>
                  <span style={{ display: "flex", alignItems: "center", gap: 6, minWidth: 0, justifyContent: "flex-end" }}>
                    <span style={{ fontSize: 13, fontWeight: fav === "away" ? 800 : 600, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{away.name}</span>
                    <Crest url={away.logo_url} name={away.name} size={22} />
                  </span>
                </div>
                <OddsBar home={g.home} draw={g.draw} away={g.away} />
                <div style={{ display: "flex", justifyContent: "space-between", fontSize: 11, color: C.muted }}>
                  <span>Win {pct(g.home)}</span>
                  <span>Draw {pct(g.draw)}</span>
                  <span>Win {pct(g.away)}</span>
                </div>
              </div>
            );
          })}
        </section>
      )}
      {table && (
        <section style={panel}>
          <h2 style={sectionTitle}>PROJECTED FINAL TABLE</h2>
          <div style={{ display: "grid", gridTemplateColumns: "24px 1fr 44px 52px 52px", color: C.muted, fontSize: 11, fontWeight: 800, letterSpacing: 1, paddingBottom: 6 }}>
            <span>#</span>
            <span>TEAM</span>
            <span style={{ textAlign: "center" }}>PTS</span>
            <span style={{ textAlign: "right" }}>TITLE</span>
            <span style={{ textAlign: "right" }}>TOP 3</span>
          </div>
          {table.map((r, i) => (
            <div key={r.teamId} style={{ display: "grid", gridTemplateColumns: "24px 1fr 44px 52px 52px", alignItems: "center", padding: "7px 0", borderTop: `1px solid ${C.border}`, fontSize: 13 }}>
              <span style={{ color: C.muted, fontWeight: 800 }}>{i + 1}</span>
              <span style={{ display: "flex", alignItems: "center", gap: 6, minWidth: 0 }}>
                <Crest url={r.logoUrl} name={r.name} size={20} />
                <span style={{ flex: 1, minWidth: 0, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", fontWeight: 700 }}>{r.name}</span>
              </span>
              <span style={{ textAlign: "center", fontWeight: 800 }}>{Math.round(r.projectedPoints)}</span>
              <span style={{ textAlign: "right", fontWeight: 800, color: r.title >= 0.25 ? C.win : r.title < 0.02 ? C.muted : C.text }}>{pct(r.title)}</span>
              <span style={{ textAlign: "right", color: r.top3 >= 0.5 ? C.win : r.top3 < 0.1 ? C.loss : C.text }}>{pct(r.top3)}</span>
            </div>
          ))}
          <div style={{ color: C.muted, fontSize: 11, lineHeight: 1.5, marginTop: 10 }}>
            Simulated 5,000 times from goals scored and conceded so far, using the {predictions.remainingCount} scheduled game
            {predictions.remainingCount === 1 ? "" : "s"} left. Early-season numbers move a lot week to week.
          </div>
        </section>
      )}
    </div>
  );
}

const RECORD_TONES = {
  "Most goals scored": "gold",
  "Highest-scoring game (season)": "gold",
  "Best goal difference": "good",
  "Fewest conceded per game": "good",
  "Most clean sheets": "good",
  "Biggest climber": "good",
  "Longest winning run": "good",
  "Longest unbeaten run": "good",
  "Biggest win (season)": "good",
  "Most conceded": "bad",
};

function recordRows(report) {
  const ts = report.teamStats;
  const teamNames = (l) => joinNames(l.entries.map((e) => e.name));
  const rows = [
    ["Most goals scored", ts.mostGoalsFor, (v) => v, teamNames],
    ["Best goal difference", ts.bestGoalDifference, (v) => (v > 0 ? `+${v}` : v), teamNames],
    ["Fewest conceded per game", ts.bestDefence, (v) => v.toFixed(2), teamNames],
    ["Most conceded", ts.mostGoalsAgainst, (v) => v, teamNames],
    ["Most clean sheets", ts.mostCleanSheets, (v) => v, teamNames],
    ["Most points this matchday", ts.mostMatchdayPoints, (v) => v, teamNames],
    ["Biggest climber", ts.biggestClimber, (v) => `▲${v}`, teamNames],
    ["Longest winning run", ts.longestWinningRun, (v) => v, teamNames],
    ["Longest unbeaten run", ts.longestUnbeatenRun, (v) => v, teamNames],
  ];
  const fmtMatch = (m) => `${m.homeName} ${m.homeScore}–${m.awayScore} ${m.awayName}`;
  const matchRows = [
    ["Highest-scoring game (season)", report.seasonMatches.highestScoring, (v) => `${v} goals`, (l) => l.entries.map(fmtMatch).join(" · ")],
    ["Biggest win (season)", report.seasonMatches.biggestWin, (v) => `+${v}`, (l) => l.entries.map(fmtMatch).join(" · ")],
  ];
  return [...rows, ...matchRows]
    .filter(([, l]) => l.entries.length > 0 && l.value !== null)
    .map(([label, l, fmt, who]) => ({ label, value: fmt(l.value), who: who(l), joint: l.entries.length > 1, tone: RECORD_TONES[label] }));
}

function scorerNote(gb) {
  if (gb.overRecorded) return "Scorer records add up to more than a final score, so the race isn't shown until that's fixed.";
  if (gb.entries.length === 0) return "No goalscorers recorded yet.";
  if (!gb.confirmed)
    return `Leader not confirmed: ${gb.totalUnattributed} goal${gb.totalUnattributed === 1 ? " has" : "s have"} no recorded scorer, which could change the order.`;
  return null;
}

function MatchdayResults({ report, teamsById }) {
  if (report.matchdayFixtures.length === 0) return <div style={{ color: C.muted }}>No fixtures on this matchday.</div>;
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
      {report.matchdayFixtures.map((f) => {
        const home = teamsById[f.homeTeamId] || { name: "TBD" };
        const away = teamsById[f.awayTeamId] || { name: "TBD" };
        const center =
          f.status === "completed"
            ? `${f.homeScore} – ${f.awayScore}`
            : f.status === "forfeit"
            ? "FF"
            : f.status === "incomplete"
            ? "?"
            : "vs";
        const note = { forfeit: "Forfeit · 0 pts each", incomplete: "Score missing", pending: "Not played / no result" }[f.status];
        return (
          <div key={f.id} style={{ display: "grid", gridTemplateColumns: "1fr auto 1fr", gap: 8, alignItems: "center", padding: "8px 0", borderTop: `1px solid ${C.border}` }}>
            <span style={{ display: "flex", alignItems: "center", gap: 8, justifyContent: "flex-end", minWidth: 0, textAlign: "right" }}>
              <span style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", fontWeight: 700, fontSize: 13 }}>{home.name}</span>
              <Crest url={home.logo_url} name={home.name} size={24} />
            </span>
            <span style={{ textAlign: "center", minWidth: 64 }}>
              <span style={{ display: "block", fontWeight: 900, fontSize: 16, color: f.status === "forfeit" ? C.forfeit : C.text }}>{center}</span>
              {note && <span style={{ display: "block", fontSize: 10, color: C.muted }}>{note}</span>}
            </span>
            <span style={{ display: "flex", alignItems: "center", gap: 8, minWidth: 0 }}>
              <Crest url={away.logo_url} name={away.name} size={24} />
              <span style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", fontWeight: 700, fontSize: 13 }}>{away.name}</span>
            </span>
          </div>
        );
      })}
    </div>
  );
}

const isWebKit = () => {
  if (typeof navigator === "undefined") return false;
  const ua = navigator.userAgent;
  const iOS = /iP(hone|ad|od)/.test(ua) || (/Macintosh/.test(ua) && navigator.maxTouchPoints > 1);
  return iOS || /^((?!chrome|android).)*safari/i.test(ua);
};

async function waitForImages(node) {
  const imgs = [...node.querySelectorAll("img")];
  await Promise.all(imgs.map((img) => (img.decode ? img.decode().catch(() => {}) : Promise.resolve())));
}

async function dataUrlToFile(dataUrl, fileName) {
  const blob = await (await fetch(dataUrl)).blob();
  return new File([blob], fileName, { type: "image/png" });
}

function saveFile(file) {
  const href = URL.createObjectURL(file);
  const a = document.createElement("a");
  a.href = href;
  a.download = file.name;
  a.rel = "noopener";
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(href), 60000);
}

const isTouchDevice = () =>
  typeof window !== "undefined" && (window.matchMedia?.("(pointer: coarse)").matches || navigator.maxTouchPoints > 0);

function ExportDialog({ open, onClose, posterRef, fileName, prepare }) {
  const [state, setState] = useState({ status: "idle", url: null, error: null });
  const [file, setFile] = useState(null);
  const [saveError, setSaveError] = useState(null);

  useEffect(() => {
    setFile(null);
    setSaveError(null);
    if (state.status !== "ready") return undefined;
    let cancelled = false;
    dataUrlToFile(state.url, fileName)
      .then((f) => !cancelled && setFile(f))
      .catch((e) => !cancelled && setSaveError(e?.message || String(e)));
    return () => {
      cancelled = true;
    };
  }, [state, fileName]);

  // Called straight from the click so Safari keeps the user gesture that share() needs.
  const onSave = () => {
    if (!file) return;
    setSaveError(null);
    const canShare = isTouchDevice() && navigator.canShare?.({ files: [file] });
    if (canShare) {
      navigator.share({ files: [file], title: "HSL Insights", text: `https://stats.hadafsports.com/insights${window.location.search}` }).catch((e) => {
        if (e?.name !== "AbortError") saveFile(file);
      });
      return;
    }
    try {
      saveFile(file);
    } catch (e) {
      setSaveError(e?.message || String(e));
    }
  };

  useEffect(() => {
    if (!open) return undefined;
    let cancelled = false;
    setState({ status: "working", url: null, error: null });
    (async () => {
      try {
        await prepare();
        if (document.fonts?.ready) await document.fonts.ready;
        await waitForImages(posterRef.current);
        const opts = { width: POSTER_WIDTH, height: POSTER_HEIGHT, pixelRatio: 1, backgroundColor: C.bg };
        const render = async () => {
          try {
            return await toPng(posterRef.current, opts);
          } catch (e) {
            return toPng(posterRef.current, { ...opts, skipFonts: true });
          }
        };
        // WebKit (Safari and every iOS browser) often paints images only on the second capture.
        if (isWebKit()) await render();
        const url = await render();
        if (!cancelled) setState({ status: "ready", url, error: null });
      } catch (e) {
        if (!cancelled) setState({ status: "error", url: null, error: e?.message || String(e) });
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [open, posterRef, prepare]);

  if (!open) return null;
  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label="Instagram poster preview"
      onClick={onClose}
      style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.55)", zIndex: 1400, display: "flex", alignItems: "center", justifyContent: "center", padding: 16 }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{ ...panel, background: C.bg, width: "min(520px, 100%)", maxHeight: "100%", overflow: "auto", display: "flex", flexDirection: "column", gap: 12 }}
      >
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
          <b>Instagram poster · 1080 × 1440</b>
          <Button onClick={onClose} ariaLabel="Close preview">
            ✕
          </Button>
        </div>
        <div style={{ aspectRatio: "3 / 4", width: "100%", background: C.panel, borderRadius: 10, display: "flex", alignItems: "center", justifyContent: "center", overflow: "hidden" }}>
          {state.status === "working" && <span style={{ color: C.muted }}>Rendering poster…</span>}
          {state.status === "error" && <span style={{ color: C.loss, padding: 16 }}>Could not render the poster: {state.error}</span>}
          {state.status === "ready" && <img src={state.url} alt="Poster preview" style={{ width: "100%", height: "100%", display: "block" }} />}
        </div>
        {state.status === "ready" && (
          <div style={{ color: C.muted, fontSize: 12 }}>
            {saveError ? <span style={{ color: C.loss }}>Couldn't save: {saveError}. </span> : null}
            On a phone you can also press and hold the preview to save it to Photos.
          </div>
        )}
        <div style={{ display: "flex", gap: 8, justifyContent: "flex-end" }}>
          <Button onClick={onClose}>Cancel</Button>
          <Button primary onClick={onSave} disabled={!file}>
            {isTouchDevice() && typeof navigator.share === "function" ? "Save / share" : "Download PNG"}
          </Button>
        </div>
      </div>
    </div>
  );
}

const slug = (s) => String(s).toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "");

export default function Insights() {
  const [params, setParams] = useSearchParams();
  const [options, setOptions] = useState(null);
  const [optionsError, setOptionsError] = useState(null);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [dataError, setDataError] = useState(null);
  const [exportOpen, setExportOpen] = useState(false);
  const posterRef = useRef(null);

  useEffect(() => {
    let cancelled = false;
    fetchInsightOptions()
      .then((o) => !cancelled && setOptions(o))
      .catch((e) => !cancelled && setOptionsError(e?.message || String(e)));
    return () => {
      cancelled = true;
    };
  }, []);

  const update = useCallback(
    (patch) => {
      const next = new URLSearchParams(params);
      Object.entries(patch).forEach(([k, v]) => (v === null || v === undefined || v === "" ? next.delete(k) : next.set(k, String(v))));
      setParams(next, { replace: true });
    },
    [params, setParams]
  );

  const season = params.get("season");
  const city = params.get("city");
  const division = params.get("division");
  const mdParam = params.get("matchday");

  const seasonOptions = useMemo(() => (options ? options.options.filter((o) => o.season === season) : []), [options, season]);
  const valid = seasonOptions.some((o) => o.city === city && o.division === division);

  // Fill in missing / unknown selections with the newest season and its first division.
  useEffect(() => {
    if (!options || options.seasons.length === 0) return;
    const s = options.seasons.includes(season) ? season : options.seasons[0];
    const inSeason = options.options.filter((o) => o.season === s);
    if (s === season && inSeason.some((o) => o.city === city && o.division === division)) return;
    const pick =
      inSeason.find((o) => o.city === city && o.division === division) ||
      inSeason.find((o) => o.city === city) ||
      [...inSeason].sort((a, b) => a.city.localeCompare(b.city) || a.division.localeCompare(b.division, undefined, { numeric: true }))[0];
    update({ season: s, city: pick.city, division: pick.division, matchday: null });
  }, [options, season, city, division, update]);

  useEffect(() => {
    if (!valid) return undefined;
    let cancelled = false;
    setLoading(true);
    setDataError(null);
    fetchDivisionData({ season, city, division })
      .then((d) => !cancelled && setData({ key: `${season}|${city}|${division}`, ...d }))
      .catch((e) => !cancelled && setDataError(e?.message || String(e)))
      .finally(() => !cancelled && setLoading(false));
    return () => {
      cancelled = true;
    };
  }, [valid, season, city, division]);

  const currentData = data && data.key === `${season}|${city}|${division}` ? data : null;
  const teamsById = useMemo(() => Object.fromEntries((currentData?.teams || []).map((t) => [t.id, t])), [currentData]);

  const report = useMemo(
    () => (currentData && currentData.fixtures.length ? buildWeeklyReport({ ...currentData, matchday: mdParam ? Number(mdParam) : undefined }) : null),
    [currentData, mdParam]
  );
  const previousReport = useMemo(
    () => (currentData && report && report.matchday > 1 ? buildWeeklyReport({ ...currentData, matchday: report.matchday - 1 }) : null),
    [currentData, report]
  );
  const predictFor = useCallback(
    (rep) => {
      if (!currentData || !rep || !rep.hasResults) return null;
      const { matchdays, mdByFixtureId } = buildMatchdays(currentData.fixtures);
      const p = buildPredictions({ fixtures: currentData.fixtures, standings: rep.standings, mdByFixtureId, matchday: rep.matchday, matchdays, sims: 5000 });
      return p;
    },
    [currentData]
  );
  const predictions = useMemo(() => predictFor(report), [predictFor, report]);

  // The poster is a season overview, always as of the latest matchday with results.
  const posterReport = useMemo(() => {
    if (!report || !report.latestMatchday) return null;
    return report.matchday === report.latestMatchday ? report : buildWeeklyReport({ ...currentData, matchday: report.latestMatchday });
  }, [report, currentData]);
  const posterPredictions = useMemo(
    () => (posterReport === report ? predictions : predictFor(posterReport)),
    [posterReport, report, predictions, predictFor]
  );
  const [posterImages, setPosterImages] = useState({});
  const highlights = useMemo(() => (report && report.hasResults ? selectHighlights(report, previousReport, teamsById, 4) : []), [report, previousReport, teamsById]);


  const cities = [...new Set(seasonOptions.map((o) => o.city))].sort();
  const divisions = seasonOptions.filter((o) => o.city === city).map((o) => o.division).sort((a, b) => a.localeCompare(b, undefined, { numeric: true }));
  const maxSelectable = report ? Math.max(1, report.latestMatchday) : 1;

  const meta = {
    divisionLabel: divisionLabel(division),
    seasonLabel: seasonLabel(season),
    city: city || "",
    url: `${window.location.host}/insights`,
  };

  const goMd = (m) => update({ matchday: m });

  const preparePoster = useCallback(async () => {
    if (!posterReport) return;
    const urls = [hslLogo, ...posterReport.standings.map((r) => r.logoUrl)];
    const map = await inlineImages(urls);
    setPosterImages(map);
    await new Promise((r) => setTimeout(r, 60));
  }, [posterReport]);

  return (
    <div style={{ background: C.bg, color: C.text, fontFamily: FONT, minHeight: "100vh", padding: "20px 16px 48px" }}>
      <div style={{ maxWidth: 1180, margin: "0 auto", display: "flex", flexDirection: "column", gap: 16 }}>
        <header style={{ display: "flex", flexWrap: "wrap", alignItems: "flex-end", gap: 12, justifyContent: "space-between" }}>
          <div style={{ minWidth: 0 }}>
            <div style={{ color: C.muted, fontSize: 12, fontWeight: 800, letterSpacing: 3 }}>HSL INSIGHTS</div>
            <h1 style={{ margin: "4px 0 0", fontSize: "clamp(22px, 5vw, 34px)", fontWeight: 900, letterSpacing: -0.5, textTransform: "uppercase", lineHeight: 1.1 }}>
              {city ? `${city} ${meta.divisionLabel}` : "Weekly analytics"}
              {report && <span style={{ color: C.muted }}> — Matchday {pad2(report.matchday)}</span>}
            </h1>
            {report && (
              <div style={{ color: C.muted, fontSize: 14, marginTop: 4 }}>
                {formatMatchdayDate(report.matchdayDate)} · {meta.seasonLabel}
              </div>
            )}
          </div>
          {posterReport && (
            <Button primary onClick={() => setExportOpen(true)}>
              Share season poster
            </Button>
          )}
        </header>

        <div style={{ ...panel, display: "flex", flexWrap: "wrap", gap: 12, alignItems: "flex-end" }}>
          <Select label="SEASON" value={season || ""} onChange={(v) => update({ season: v, matchday: null })} disabled={!options}>
            {(options?.seasons || []).map((s) => (
              <option key={s} value={s}>
                {seasonLabel(s)}
              </option>
            ))}
          </Select>
          <Select label="CITY" value={city || ""} onChange={(v) => update({ city: v, division: null, matchday: null })} disabled={!options}>
            {cities.map((c) => (
              <option key={c} value={c}>
                {c}
              </option>
            ))}
          </Select>
          <Select label="DIVISION" value={division || ""} onChange={(v) => update({ division: v, matchday: null })} disabled={!options}>
            {divisions.map((d) => (
              <option key={d} value={d}>
                {divisionLabel(d)}
              </option>
            ))}
          </Select>
          <div style={{ display: "flex", gap: 6, alignItems: "flex-end", flex: "1 1 240px" }}>
            <Button ariaLabel="Previous matchday" onClick={() => goMd(report.matchday - 1)} disabled={!report || report.matchday <= 1}>
              ‹
            </Button>
            <Select label="MATCHDAY" value={report ? String(report.matchday) : ""} onChange={(v) => goMd(v)} disabled={!report}>
              {(report?.matchdays || []).map((m) => (
                <option key={m.matchday} value={m.matchday} disabled={m.matchday > maxSelectable}>
                  MD{pad2(m.matchday)} · {formatMatchdayDate(m.date, { month: "short", day: "numeric" })}
                  {m.reportable ? "" : " (no results)"}
                </option>
              ))}
            </Select>
            <Button ariaLabel="Next matchday" onClick={() => goMd(report.matchday + 1)} disabled={!report || report.matchday >= maxSelectable}>
              ›
            </Button>
          </div>
        </div>

        {optionsError && <Notice tone="error">Could not load seasons: {optionsError}</Notice>}
        {dataError && <Notice tone="error">Could not load this division: {dataError}</Notice>}
        {options && options.seasons.length === 0 && <Notice>No soccer fixtures have been scheduled yet.</Notice>}
        {(loading || (!options && !optionsError)) && <div style={{ color: C.muted, padding: 24, textAlign: "center" }}>Loading insights…</div>}
        {!loading && currentData && !report && <Notice>No fixtures have been scheduled for this division yet.</Notice>}

        {report && !loading && <Report report={report} highlights={highlights} teamsById={teamsById} predictions={predictions} />}
      </div>

      {posterReport && (
        <>
          <div aria-hidden style={{ position: "fixed", left: -20000, top: 0, pointerEvents: "none" }}>
            <Poster
              ref={posterRef}
              report={posterReport}
              meta={meta}
              images={posterImages}
              predictions={posterPredictions}
              teamsById={teamsById}
            />
          </div>
          <ExportDialog
            open={exportOpen}
            onClose={() => setExportOpen(false)}
            posterRef={posterRef}
            prepare={preparePoster}
            fileName={`hsl-insights-${slug(season)}-${slug(city)}-${slug(division)}-season-md${pad2(posterReport.matchday)}.png`}
          />
        </>
      )}
    </div>
  );
}

function ForecastLegend({ matchday, size = 12 }) {
  return (
    <span style={{ color: C.forecast, fontWeight: 700, marginLeft: 8, whiteSpace: "nowrap", fontSize: size }}>
      <svg width={size * 2} height={size * 0.6} style={{ marginRight: 4, verticalAlign: "middle" }}>
        <line x1="0" y1={size * 0.3} x2={size * 2} y2={size * 0.3} stroke={C.forecast} strokeWidth="2" strokeDasharray="4 3" />
      </svg>
      Forecast for MD{matchday} from predicted scores
    </span>
  );
}

function Report({ report, highlights, teamsById, predictions }) {
  const forecast = forecastNextMatchday(report.standings, predictions);
  const records = recordRows(report);
  const gbNote = scorerNote(report.goldenBoot);
  const gb = report.goldenBoot;
  const counts = report.matchdayMatches.counts;
  const v = report.validation;

  if (!report.hasResults) {
    return (
      <Notice tone="warn">
        No results have been recorded for Matchday {report.matchday} ({formatMatchdayDate(report.matchdayDate)}). The games may not have
        been played yet, may have been postponed, or the results have not been entered.
      </Notice>
    );
  }

  return (
    <>
      {v.checked && v.mismatches.length > 0 && (
        <Notice tone="error">
          Calculated totals don't match the official standings for {joinNames(v.mismatches.map((m) => m.name))}. Check the fixture
          results before sharing.
        </Notice>
      )}
      {(counts.pending > 0 || counts.incomplete > 0) && (
        <Notice tone="warn">
          {counts.pending > 0 && `${counts.pending} game${counts.pending === 1 ? " has" : "s have"} no result yet on this matchday. `}
          {counts.incomplete > 0 && `${counts.incomplete} game${counts.incomplete === 1 ? " is" : "s are"} marked played but missing a score and ${counts.incomplete === 1 ? "is" : "are"} left out. `}
          Figures cover recorded results only.
        </Notice>
      )}

      <section style={panel}>
        <h2 style={sectionTitle}>
          POINTS RACE <span style={{ color: C.accent }}>· AFTER MD{report.matchday} OF {report.matchdays.length}</span>
          {forecast && <ForecastLegend matchday={forecast.matchday} />}
        </h2>
        <PointsChart report={report} forecast={forecast} height={340} />
      </section>

      {highlights.length > 0 && (
        <section style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))", gap: 12 }}>
          {highlights.map((h) => (
            <HighlightCard key={h.key} h={h} />
          ))}
        </section>
      )}

      <section style={{ ...panel, overflowX: "auto" }}>
        <h2 style={sectionTitle}>STANDINGS AFTER MD{report.matchday}</h2>
        <div style={{ minWidth: 660 }}>
          <StandingsTable rows={report.standings} />
        </div>
      </section>

      <section style={panel}>
        <h2 style={sectionTitle}>MATCHDAY {report.matchday} RESULTS</h2>
        <MatchdayResults report={report} teamsById={teamsById} />
      </section>

      <Predictions predictions={predictions} teamsById={teamsById} />

      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(min(100%, 360px), 1fr))", gap: 16 }}>
        <section style={panel}>
          <h2 style={sectionTitle}>SEASON RECORDS TO MD{report.matchday}</h2>
          {records.map((r) => (
            <div key={r.label} style={{ display: "grid", gridTemplateColumns: "1fr auto", gap: 8, padding: "8px 0", borderTop: `1px solid ${C.border}` }}>
              <div style={{ minWidth: 0 }}>
                <div style={{ color: C.muted, fontSize: 12 }}>
                  {r.label}
                  {r.joint && <span style={{ color: C.gold }}> · joint</span>}
                </div>
                <div style={{ fontWeight: 700, fontSize: 14 }}>{r.who}</div>
              </div>
              <div style={{ fontWeight: 900, fontSize: 18, color: toneColor(r.tone) }}>{r.value}</div>
            </div>
          ))}
        </section>
        <section style={panel}>
          <h2 style={{ ...sectionTitle, color: C.gold }}>GOLDEN BOOT RACE</h2>
          {report.goldenBootRace.entries.length > 0 && (
            <div style={{ marginBottom: 12 }}>
              <RaceList entries={report.goldenBootRace.entries} teamsById={teamsById} size={28} />
              {report.goldenBootRace.tiedBeyond > 0 && (
                <div style={{ color: C.muted, fontSize: 12, marginTop: 6 }}>
                  +{report.goldenBootRace.tiedBeyond} more on {report.goldenBootRace.entries[report.goldenBootRace.entries.length - 1].goals}
                </div>
              )}
            </div>
          )}
          {gbNote && <div style={{ color: C.muted, fontSize: 13, lineHeight: 1.5 }}>{gbNote}</div>}
          <h2 style={{ ...sectionTitle, marginTop: 18 }}>DATA NOTES</h2>
          <ul style={{ color: C.muted, fontSize: 12, lineHeight: 1.6, margin: 0, paddingLeft: 18 }}>
            <li>Matchdays are fixture dates in order. Win 3, draw 1, loss 0; ties broken by goal difference, then goals scored.</li>
            <li>A forfeit counts as a game played with 0 points and no goals for either team, and ends winning and unbeaten runs.</li>
            {counts.forfeit > 0 && <li>{counts.forfeit} forfeit{counts.forfeit === 1 ? "" : "s"} on this matchday.</li>}
            {gb.totalUnattributed > 0 && <li>{gb.totalUnattributed} goal{gb.totalUnattributed === 1 ? "" : "s"} so far have no recorded scorer.</li>}
            <li>Assists aren't recorded per match, so there is no assist leader by matchday.</li>
            <li>
              {v.checked
                ? v.mismatches.length === 0
                  ? "Totals match the official standings."
                  : `${v.mismatches.length} team${v.mismatches.length === 1 ? "" : "s"} differ from the official standings.`
                : "Official standings were not available to check against."}
            </li>
          </ul>
        </section>
      </div>
    </>
  );
}
